import { CircleAlert, Code, Wrench } from "lucide-react";

import type { AgenticLogicActiveRepairRun } from "@intelligems/ig-types";
import type { AgenticLogicDataSurface } from "@intelligems/ig-utils";
import {
  IgBorderColors,
  IgCoreTextColors,
} from "@intelligems/ig-design-tokens";
import { IgButton, IgPaper, IgTypography } from "@intelligems/ig-theme";
import { Box, Stack } from "@mui/material";
import { useRestoreAgenticLogicRepairBaseline } from "@src/api/agentic-logic/restore-agentic-logic-repair-baseline";
import { useAgenticLogicCreationContextBuilder } from "@src/features/ai/components/AiChat/useAgenticLogicCreationContext";
import { useDispatch } from "@src/store";
import { setAiChat } from "@src/store/slices/global";

/**
 * Copy for a give-up with no model-authored diagnosis (the diagnose pass is
 * best-effort). Keyed on the deterministic stop cause so the panel is never
 * blank.
 */
const CAUSE_FALLBACK: Record<string, string> = {
  max_attempts:
    "Each fix cleared one problem but another check kept failing. The rule needs a structural change, not another rewrite.",
  deadline:
    "The fixes were still in progress when the time limit ran out. The failing check is likely one that takes minutes per run.",
  cost_budget:
    "The fix budget for this rule was used up before the checks went green.",
  repair_unavailable:
    "Automatic fixing is not available right now, so the failing checks were left as they are.",
  stale:
    "The fix loop lost track of this rule — likely an infrastructure hiccup. Re-run the tests to start fresh.",
  draft_changed:
    "You edited the code while a fix was in progress, so the loop stood down rather than overwrite your changes.",
  rule_fenced: "This rule can no longer be published, so fixing stopped.",
  not_source_repairable:
    "The failing check is one that rewriting the rule cannot fix, so the loop stood down rather than change working code to satisfy it.",
};

/**
 * The non-convergence product: shown above the gate rail whenever the repair
 * run stopped without publishing — it gave up against its bounds, or it was
 * stood down (the merchant edited the draft mid-fix; the sweep forced a run
 * terminal after its worker died). Not a toast — it must survive a reload, and
 * it carries the valid ways forward: try an alternative through Atlas, and,
 * when an earlier published version exists, restore the pre-repair draft. The
 * variable copy is model-authored and grounded; this component only frames it.
 */
export const RepairGiveUpPanel = ({
  ruleId,
  run,
  authorName,
  advancedEnabled,
  canRestoreBaseline,
  dataSurface,
  onOpenCodeWorkspace,
  embedded = false,
  disabled = false,
}: {
  ruleId: string;
  run: AgenticLogicActiveRepairRun;
  authorName: string;
  canRestoreBaseline: boolean;
  /**
   * The org's Advanced verdict. A prop, not a hook: this panel is a pure
   * frame around model-authored copy and is rendered directly by its spec.
   */
  advancedEnabled: boolean;
  /**
   * The acting user's org-data surface, which types the SDK in the Atlas
   * session "Try an alternative" opens. A prop for the same reason.
   */
  dataSurface: AgenticLogicDataSurface;
  onOpenCodeWorkspace: () => void;
  embedded?: boolean;
  disabled?: boolean;
}) => {
  const dispatch = useDispatch();
  const restoreBaseline = useRestoreAgenticLogicRepairBaseline();
  const buildCodeEntryCreationContext =
    useAgenticLogicCreationContextBuilder(dataSurface);
  const giveUp = run.giveUp;
  if (!giveUp) {
    return null;
  }

  const diagnosis =
    giveUp.diagnosis ??
    CAUSE_FALLBACK[giveUp.cause] ??
    "The checks kept failing in a way the fixes could not resolve.";
  const proposal = giveUp.counterProposal;

  const askAtlas = () => {
    // Prefills the always-open Atlas rail with the counter-proposal as a
    // fresh creation session — the same path a first message in the idle
    // chat takes.
    // The builder is async only because an edit session must first read the
    // rule's draft; this counter-proposal opens a NEW rule, names no rule id,
    // and so resolves without a request.
    void buildCodeEntryCreationContext({
      prompt: proposal
        ? `${proposal.summary} (This replaces a rule whose checks kept failing: ${diagnosis})`
        : `Build a simpler variant of this rule — the previous attempt kept failing its checks: ${diagnosis}`,
    }).then((context) => {
      dispatch(
        setAiChat([
          { key: "open", value: true },
          { key: "mode", value: "agentic-logic-creation" },
          { key: "initialContext", value: context },
          { key: "start", value: true },
        ])
      );
    });
  };

  const headline =
    (run.status === "abandoned"
      ? `${authorName} stopped working on this rule before it finished`
      : `${authorName} couldn't get this rule past its checks`) +
    (run.attempt > 0
      ? ` after ${run.attempt} ${run.attempt === 1 ? "fix" : "fixes"}`
      : "");

  return (
    // Same card, margins, and header-row grammar as the `GateProgressStrip`
    // that renders directly beneath it, so the two read as one rail: icon,
    // bold headline, spacer, then the actions as extra-small secondary
    // buttons. The story lives in the body below the divider.
    <IgPaper
      component={embedded ? "div" : "section"}
      role={embedded ? undefined : "region"}
      aria-label={embedded ? undefined : "Automatic fixing stopped"}
      sx={embedded ? { m: 0, p: 0, border: "none" } : { mt: 2, mx: 2, p: 0 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={
          embedded
            ? { flexWrap: "wrap", gap: 1 }
            : { minHeight: 52, px: 3.5, py: 2.5 }
        }
      >
        {!embedded ? (
          <>
            <CircleAlert
              size={16}
              color={IgCoreTextColors.tonal.warning}
              aria-hidden
            />
            <IgTypography
              variant="body2"
              sx={{
                color: IgCoreTextColors.primary,
                fontWeight: 600,
                minWidth: 0,
              }}
            >
              {/* An abandoned run reached no verdict on the rule — saying it
              "couldn't get past its checks" would blame the rule for the
              platform standing down. */}
              {headline}
            </IgTypography>

            <Box sx={{ flex: 1 }} />
          </>
        ) : null}

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          role="group"
          aria-label="Stopped fix actions"
          sx={{ flexShrink: 0, flexWrap: "wrap", gap: 1 }}
        >
          {canRestoreBaseline && run.baselineDraftS3Key ? (
            <IgButton
              size="extraSmall"
              variant="text"
              color="secondary"
              disabled={disabled || restoreBaseline.isPending}
              onClick={() =>
                restoreBaseline.mutate({
                  body: undefined,
                  pathParams: { ruleId },
                  queryParams: undefined,
                })
              }
              sx={{ whiteSpace: "nowrap" }}
            >
              {embedded
                ? "Keep previous logic"
                : advancedEnabled
                  ? `Restore the version before ${authorName}'s fixes`
                  : `Discard ${authorName}'s changes`}
            </IgButton>
          ) : null}
          {proposal && proposal.actionKind !== "manual_edit" ? (
            <IgButton
              size="extraSmall"
              variant="outlined"
              color="secondary"
              startIcon={<Wrench size={16} />}
              onClick={askAtlas}
              disabled={disabled}
              sx={{ whiteSpace: "nowrap" }}
            >
              {embedded
                ? "Try an alternative"
                : `Ask ${authorName} to try this instead`}
            </IgButton>
          ) : null}
          {/* The code workspace IS the Advanced tab. Without it the button
              navigates nowhere, so a basic org is left with the Atlas route —
              which is the only one they could have taken anyway. */}
          {proposal?.actionKind === "manual_edit" && advancedEnabled ? (
            <IgButton
              size="extraSmall"
              variant="outlined"
              color="secondary"
              startIcon={<Code size={16} />}
              onClick={onOpenCodeWorkspace}
              sx={{ whiteSpace: "nowrap" }}
            >
              Open the code workspace
            </IgButton>
          ) : null}
        </Stack>
      </Stack>

      <Stack
        spacing={1}
        sx={{
          borderTop: embedded
            ? "none"
            : `1px solid ${IgBorderColors.core.default}`,
          px: embedded ? 0 : 3.5,
          py: embedded ? 1 : 2.5,
        }}
      >
        <IgTypography
          variant="body2"
          sx={{ color: IgCoreTextColors.secondary }}
        >
          {diagnosis}
        </IgTypography>
        {proposal ? (
          <IgTypography
            variant="body2"
            sx={{ color: IgCoreTextColors.secondary }}
          >
            <Box
              component="span"
              sx={{ color: IgCoreTextColors.primary, fontWeight: 600 }}
            >
              What {authorName} can build instead:
            </Box>{" "}
            {proposal.summary} <em>({proposal.tradeoff})</em>
          </IgTypography>
        ) : null}
      </Stack>
    </IgPaper>
  );
};
